import { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import { useLogin } from '../hooks/useLogin'

export function LoginPage() {
  const navigate      = useNavigate()
  const { checkAuth } = useAuthStore()
  const {
    email, setEmail, password, setPassword,
    newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    mfaCode, setMfaCode,
    error, loading, challenge,
    submitLogin, submitNewPassword, submitMfa,
    resetChallenge,
  } = useLogin()

  const onDone = async () => { await checkAuth(); navigate('/profile') }

  const handleLogin       = (e: FormEvent) => { e.preventDefault(); submitLogin(onDone) }
  const handleNewPassword = (e: FormEvent) => { e.preventDefault(); submitNewPassword(onDone) }
  const handleMfa         = (e: FormEvent) => { e.preventDefault(); submitMfa(onDone) }

  // ─── MFA challenge ─────────────────────────────────────────────────────────

  if (challenge === 'mfa') {
    return (
      <div className="auth-card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h1>
          <p className="text-gray-600">Enter the 6-digit code from your authenticator app</p>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleMfa} className="space-y-4">
          <div>
            <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
              Authentication Code
            </label>
            <input
              id="mfaCode"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
              className="auth-input text-center text-2xl tracking-widest"
              placeholder="000000"
              autoComplete="one-time-code"
              autoFocus
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading || mfaCode.length !== 6}
            className="auth-button disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
        <p className="mt-4 text-center">
          <button type="button" onClick={resetChallenge} className="text-sm auth-link">
            ← Back to login
          </button>
        </p>
      </div>
    )
  }

  // ─── New password required ─────────────────────────────────────────────────

  if (challenge === 'new_password') {
    return (
      <div className="auth-card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Change Password</h1>
          <p className="text-gray-600">Please set a new password to continue</p>
        </div>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleNewPassword} className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="auth-input"
              placeholder="••••••••"
              required
              minLength={8}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="auth-input"
              placeholder="••••••••"
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="auth-button disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    )
  }

  // ─── Default: credentials form ─────────────────────────────────────────────

  return (
    <div className="auth-card">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
        <p className="text-gray-600">Sign in to access your SCJ services</p>
      </div>
      {error && <div className="auth-error">{error}</div>}
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="auth-input"
            placeholder="you@example.com"
            required
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="auth-input"
            placeholder="••••••••"
            required
          />
        </div>
        <div className="flex justify-end text-sm">
          <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="auth-button disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
      <div className="mt-8 pt-6 border-t border-gray-200">
        <p className="text-center text-sm text-gray-500">
          Secured by AWS Cognito with SRP + httpOnly cookies
        </p>
      </div>
    </div>
  )
}
